import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Avatar
} from '@mui/material';
import {
  Business as BusinessIcon,
  AccountBalance as CreditIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { customerService, CustomerProfile, handleApiError } from '../services';
import CustomerProfileDisplay from '../components/profile/CustomerProfileForm';
import { ShipToAddresses } from '../components/profile/ShipToAddresses';
import SalesRepCard from '../components/profile/SalesRepCard';
import { useTranslation } from 'react-i18next';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const profileData = await customerService.getProfile();
        setProfile(profileData);
      } catch (err) {
        const errorMessage = handleApiError(err);
        setError(errorMessage);
        console.error('Failed to fetch customer profile:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfile();
    }
  }, [user]);


  const getCreditUtilization = () => {
    if (!profile) return 0;
    return customerService.calculateCreditUtilization(
      profile.outstanding_balance,
      profile.credit_limit
    );
  };

  const getCreditStatusColor = () => {
    const utilization = getCreditUtilization();
    return customerService.getCreditStatusColor(utilization);
  };

  const getCreditStatusText = () => {
    const utilization = getCreditUtilization();
    return customerService.getCreditStatusText(utilization);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('profile.title')}
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('profile.title')}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('profile.notFound')}
        </Alert>
      </Box>
    );
  }

  const creditUtilization = getCreditUtilization();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={4}>
        <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
          <BusinessIcon />
        </Avatar>
        <Box>
          <Typography variant="h4" component="h1">
            {t('profile.customerProfile')}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {profile.company_name}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={4}>
        {/* Company Information */}
        <CustomerProfileDisplay profile={profile} />

        {/* Assigned sales rep — resolved from workforce cache */}
        <SalesRepCard />

        {/* Ship-to Addresses */}
        <ShipToAddresses />

        {/* Credit Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
              {t('profile.creditInformation')}
            </Typography>
            
            {/* Credit Summary Cards */}
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mb={4}>
              <Paper elevation={1} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
                <CreditIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  {customerService.formatCurrency(profile.credit_limit)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('profile.creditLimit')}
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  {customerService.formatCurrency(profile.outstanding_balance)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('profile.outstandingBalance')}
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  {customerService.formatCurrency(profile.available_credit)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('profile.availableCredit')}
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
                <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                  <Chip
                    label={getCreditStatusText()}
                    sx={{ 
                      bgcolor: getCreditStatusColor(),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  {creditUtilization.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('profile.creditUtilization')}
                </Typography>
              </Paper>
            </Box>

            {/* Credit Utilization Bar */}
            <Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  {t('profile.creditUtilizationProgress')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {creditUtilization.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(creditUtilization, 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getCreditStatusColor()
                  }
                }}
              />
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="caption" color="textSecondary">
                  0%
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  100%
                </Typography>
              </Box>
            </Box>

            {/* Credit Status Alerts */}
            {creditUtilization >= 90 && (
              <Alert severity="error" sx={{ mt: 3 }}>
                <strong>Critical:</strong> Your credit utilization is very high. 
                Please settle outstanding balance to avoid order processing delays.
              </Alert>
            )}
            {creditUtilization >= 70 && creditUtilization < 90 && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                <strong>Warning:</strong> Your credit utilization is approaching the limit. 
                Consider settling some outstanding balance.
              </Alert>
            )}
            {creditUtilization < 50 && (
              <Alert severity="success" sx={{ mt: 3 }}>
                <strong>Good:</strong> Your credit utilization is healthy. 
                You have sufficient credit available for new orders.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default ProfilePage;